import type { Thread, ThreadType, AppFeedbackKind, ThreadObjectType, StudentTeacherKind } from './types';

export interface ThreadApiClient {
    fetchApi: <T>(endpoint: string, options?: RequestInit) => Promise<T>;
}

export type ThreadImageContentType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface CreateThreadApiOptions {
    /** URL prefix for thread endpoints. Defaults to '/threads'. Admin uses '/console/threads'. */
    basePath?: string;
}

export function createThreadApi(client: ThreadApiClient, options: CreateThreadApiOptions = {}) {
    const base = options.basePath ?? '/threads';
    return {
        /** POST {base} — Create or upsert a thread. */
        create: (data: {
            id?: string;
            type: ThreadType;
            /** student_teacher: what the thread is about. */
            kind?: StudentTeacherKind;
            /** Omitted when a teacher opens feedback — the caller is the owner. */
            ownerId?: string;
            /** Teacher-opened feedback: the student it is for. */
            studentId?: string;
            objectType?: string;
            objectId?: string;
            /** Omitted when a teacher opens feedback — the server titles it. */
            title?: string;
            /** The opening text; on teacher feedback, stored as the first message. */
            description: string;
            screenshotUrls?: string[];
            context?: Record<string, unknown>;
            // app_feedback only
            feedbackKind?: AppFeedbackKind;
            appContext?: { url: string; userAgent: string };
            onBehalfOfEmail?: string;
        }) =>
            client.fetchApi<Thread>(base, {
                method: 'POST',
                body: JSON.stringify(data),
            }),

        /** GET {base} — List threads with filters. */
        list: (filters?: {
            ownerId?: string;
            createdBy?: string;
            assignedTo?: string;
            type?: string;
            kind?: StudentTeacherKind;
            status?: string;
            objectType?: string;
            objectId?: string;
            subject?: string;
            feedbackKind?: AppFeedbackKind;
            email?: string;
            /** Admin (`/console/threads`) only: narrow to one tenant within the caller's scope. */
            tenantId?: string;
            sortBy?: 'updatedAt' | 'createdAt' | 'userName';
            sortOrder?: 'asc' | 'desc';
            limit?: number;
            page?: number;
        }) => {
            const query = new URLSearchParams();
            if (filters?.ownerId) query.set('ownerId', filters.ownerId);
            if (filters?.createdBy) query.set('createdBy', filters.createdBy);
            if (filters?.assignedTo) query.set('assignedTo', filters.assignedTo);
            if (filters?.type) query.set('type', filters.type);
            if (filters?.kind) query.set('kind', filters.kind);
            if (filters?.status) query.set('status', filters.status);
            if (filters?.objectType) query.set('objectType', filters.objectType);
            if (filters?.objectId) query.set('objectId', filters.objectId);
            if (filters?.subject) query.set('subject', filters.subject);
            if (filters?.feedbackKind) query.set('feedbackKind', filters.feedbackKind);
            if (filters?.email) query.set('email', filters.email);
            if (filters?.tenantId) query.set('tenantId', filters.tenantId);
            if (filters?.sortBy) query.set('sortBy', filters.sortBy);
            if (filters?.sortOrder) query.set('sortOrder', filters.sortOrder);
            if (filters?.limit) query.set('limit', String(filters.limit));
            if (filters?.page) query.set('page', String(filters.page));
            const qs = query.toString();
            return client.fetchApi<{ items: Thread[]; total: number }>(`${base}${qs ? `?${qs}` : ''}`);
        },

        /** GET {base}/{id} — Get single thread. */
        get: (threadId: string, ownerId?: string) => {
            const qs = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : '';
            return client.fetchApi<Thread>(`${base}/${threadId}${qs}`);
        },

        /** PATCH {base}/{id} — Update thread. Per-field role gating server-side. */
        update: (threadId: string, ownerId: string, changes: {
            title?: string;
            description?: string;
            screenshotUrls?: string[];
            context?: Record<string, unknown>;
            feedbackKind?: AppFeedbackKind;
            objectType?: ThreadObjectType;
            objectId?: string;
            status?: string;
            markSeen?: boolean;
            reassignToCreatorEmail?: string;
        }) =>
            client.fetchApi<Thread>(`${base}/${threadId}`, {
                method: 'PATCH',
                body: JSON.stringify({ ownerId, ...changes }),
            }),

        /** POST {base}/{id}/messages — Add message. */
        addMessage: (threadId: string, ownerId: string, text: string, attachmentUrls?: string[], opts?: { showAsPopup?: boolean }) =>
            client.fetchApi<Thread>(`${base}/${threadId}/messages`, {
                method: 'POST',
                body: JSON.stringify({ ownerId, text, attachmentUrls, ...(opts?.showAsPopup ? { showAsPopup: true } : {}) }),
            }),

        /** PATCH {base}/{id}/messages/{msgId} — Edit own message. */
        editMessage: (threadId: string, messageId: string, ownerId: string, text: string) =>
            client.fetchApi<Thread>(`${base}/${threadId}/messages/${messageId}`, {
                method: 'PATCH',
                body: JSON.stringify({ ownerId, text }),
            }),

        /** GET {base}/unread-count — Unread count with filters. `oldest` also
         *  returns the unread thread that has waited longest, for the caller's
         *  own inbox only. */
        getUnreadCount: (filters?: {
            ownerId?: string;
            createdBy?: string;
            assignedTo?: string;
            type?: string;
            kind?: StudentTeacherKind;
            oldest?: boolean;
        }) => {
            const query = new URLSearchParams();
            if (filters?.oldest) query.set('oldest', '1');
            if (filters?.ownerId) query.set('ownerId', filters.ownerId);
            if (filters?.createdBy) query.set('createdBy', filters.createdBy);
            if (filters?.assignedTo) query.set('assignedTo', filters.assignedTo);
            if (filters?.type) query.set('type', filters.type);
            if (filters?.kind) query.set('kind', filters.kind);
            const qs = query.toString();
            return client.fetchApi<{ count: number; oldest?: { threadId: string; senderName: string; since: string } }>(
                `${base}/unread-count${qs ? `?${qs}` : ''}`,
            );
        },

        /** POST {base}/upload-image — Upload image attachment. */
        uploadImage: (threadId: string, imageBase64: string, contentType: ThreadImageContentType) =>
            client.fetchApi<{ url: string; path: string }>(`${base}/upload-image`, {
                method: 'POST',
                body: JSON.stringify({ threadId, imageBase64, contentType }),
            }),

        /** POST {base}/resolve-mine — Bulk-resolve caller's open threads matching an object. */
        resolveMine: (data: {
            type: ThreadType;
            feedbackKind?: AppFeedbackKind;
            objectType: ThreadObjectType;
            objectId: string;
            comment?: string;
        }) =>
            client.fetchApi<{ resolved: number }>(`${base}/resolve-mine`, {
                method: 'POST',
                body: JSON.stringify(data),
            }),
    };
}

export type ThreadApi = ReturnType<typeof createThreadApi>;
